import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { BiEdit, BiSearch, BiSearchAlt } from "react-icons/bi";
import { LuNewspaper } from "react-icons/lu";
import { BsChat } from "react-icons/bs";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import PageImageSelector from "../components/ui/PageImageSelector";
import api from "../api/axios";
import LoadingSpinner from "../components/LoadingSpinner";

/**
 * JSON shape expected from GET /pages/our-commitment:
 * {
 *   hero: { title, subtitle, imageUrl },
 *   sections: [
 *     { key: "buy-entirely-online", heading, paragraphs: [ ... ], imageUrl },
 *     { key: "choose-payment", heading, paragraphs: [ ... ], links: [ { text, href } ] },
 *     { key: "know-getting", heading, paragraphs: [ ... ], links: [ { text, href } ] },
 *     { key: "need-more-info", heading, paragraphs: [ ... ], links: [ { text, href } ] },
 *     { key: "need-help-choosing", heading, paragraphs: [ ... ], link: { text, href } },
 *     { key: "ready-open-road", preamble, heading, subheading, button: { text, href } }
 *   ]
 * }
 */

export default function OurCommitment() {
  const { user } = useAuth();
  const isAdmin = user?.roles.includes("super-admin");

  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingSection, setEditingSection] = useState(null);

  // hidden refs for file inputs
  const heroFileInputRef = useRef(null);
  const buyOnlineFileInputRef = useRef(null);

  useEffect(() => {
    api
      .get("/pages/our-commitment")
      .then((res) => {
        setPageContent(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError("Cannot load page content");
        setLoading(false);
      });
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-red-600">{error}</p>;
  if (!pageContent) return <p>No content</p>;

  async function saveContent(updatedContent) {
    try {
      await api.put("/pages/our-commitment", { content: updatedContent });
      setPageContent(updatedContent);
      toast.success("Saved successfully");
      setEditingSection(null);
    } catch (e) {
      console.error(e);
      toast.error("Failed to save");
    }
  }

  // Helper to upload a page image to "pages/our-commitment" prefix
  async function uploadPageImage(file, onUrlReceived) {
    const form = new FormData();
    form.append("pageKey", "our-commitment");
    form.append("images", file);

    try {
      const res = await api.post("/page-images", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newImg = res.data[0];
      onUrlReceived(newImg.url);
    } catch (err) {
      console.error("Upload failed:", err);
      toast.error("Image upload failed");
    }
  }

  // Destructure out hero and section objects with defaults:
  const hero = pageContent.hero || { title: "", subtitle: "", imageUrl: "" };
  const sections = pageContent.sections || [];

  const getSection = (key, fallback) =>
    sections.find((s) => s.key === key) || fallback;

  // 1) “Buy entirely online”
  const buyOnlineSection = getSection("buy-entirely-online", {
    key: "buy-entirely-online",
    heading: "",
    paragraphs: [""],
    imageUrl: "",
  });

  // 2) “Choose the way you pay”
  const choosePaymentSection = getSection("choose-payment", {
    key: "choose-payment",
    heading: "",
    paragraphs: [""],
    links: [{ text: "", href: "" }],
  });

  // 3) “Know what you’re getting”
  const knowGettingSection = getSection("know-getting", {
    key: "know-getting",
    heading: "",
    paragraphs: [""],
    links: [{ text: "", href: "" }],
  });

  // 4) “Need more info?”
  const needMoreInfoSection = getSection("need-more-info", {
    key: "need-more-info",
    heading: "",
    paragraphs: [""],
    links: [{ text: "", href: "" }],
  });

  // 5) “Need help choosing?”
  const needHelpChoosingSection = getSection("need-help-choosing", {
    key: "need-help-choosing",
    heading: "",
    paragraphs: [""],
    link: { text: "", href: "" },
  });

  // 6) “Ready to hit the open road?”
  const readySection = getSection("ready-open-road", {
    key: "ready-open-road",
    preamble: "",
    heading: "",
    subheading: "",
    button: { text: "", href: "" },
  });

  return (
    <>
      {/* ─── HERO SECTION ────────────────────────────────────────────────────── */}
      <section className="w-full bg-primary text-white relative py-32">
        {isAdmin && editingSection !== "hero" && (
          <BiEdit
            onClick={() => setEditingSection("hero")}
            className="absolute top-4 right-4 text-2xl text-brandAccent cursor-pointer hover:text-accent"
            title="Edit Hero"
          />
        )}
        <div className="max-w-5xl mx-auto px-4 py-16 md:py-24 flex flex-col md:flex-row items-center gap-10 md:gap-40">
          <div className="md:w-2/3">
            {editingSection === "hero" ? (
              <>
                <input
                  type="text"
                  className="text-primary w-full mb-2 px-3 py-2 text-xl font-bold rounded"
                  value={hero.title}
                  onChange={(e) => {
                    setPageContent((prev) => ({
                      ...prev,
                      hero: { ...prev.hero, title: e.target.value },
                    }));
                  }}
                />
                <textarea
                  className="text-primary w-full mb-2 px-3 py-2 rounded"
                  rows={2}
                  value={hero.subtitle}
                  onChange={(e) => {
                    setPageContent((prev) => ({
                      ...prev,
                      hero: { ...prev.hero, subtitle: e.target.value },
                    }));
                  }}
                />

                {/* Hero image selector */}
                <div className="mb-2">
                  <label className="block mb-1 text-sm font-medium text-primary">
                    Hero Image
                  </label>
                  <PageImageSelector
                    pageKey="our-commitment"
                    value={hero.imageUrl}
                    onChange={(newUrl) => {
                      if (newUrl === "__upload_new__") {
                        heroFileInputRef.current.click();
                      } else {
                        setPageContent((prev) => ({
                          ...prev,
                          hero: { ...prev.hero, imageUrl: newUrl },
                        }));
                      }
                    }}
                  />
                  <input
                    type="file"
                    accept="image/*"
                    ref={heroFileInputRef}
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      uploadPageImage(file, (url) => {
                        setPageContent((prev) => ({
                          ...prev,
                          hero: { ...prev.hero, imageUrl: url },
                        }));
                      });
                      e.target.value = null;
                    }}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    className="px-4 py-2 bg-green-600 text-white rounded"
                    onClick={() => saveContent({ ...pageContent })}
                  >
                    Save
                  </button>
                  <button
                    className="px-4 py-2 bg-gray-600 text-white rounded"
                    onClick={() => setEditingSection(null)}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <h1 className="text-brandAccent text-4xl md:text-5xl font-extrabold mb-4">
                  {hero.title}
                </h1>
                <p className="text-lg md:text-2xl">{hero.subtitle}</p>
              </>
            )}
          </div>

          <div className="md:w-1/3 mt-8 md:mt-0 flex justify-center">
            <img
              src={hero.imageUrl}
              alt={hero.title}
              className="w-full max-w-md object-contain rounded-3xl"
            />
          </div>
        </div>
      </section>

      {/* ─── “Buy entirely online” SECTION ───────────────────────────────────────────── */}
      <section className="bg-white py-12 rounded-tl-3xl relative">
        {isAdmin && editingSection !== "buy-entirely-online" && (
          <BiEdit
            onClick={() => setEditingSection("buy-entirely-online")}
            className="absolute top-4 right-4 text-xl text-brandAccent cursor-pointer hover:text-accent"
            title="Edit Buy entirely online"
          />
        )}
        <div className="max-w-5xl mx-auto px-4">
          {editingSection === "buy-entirely-online" ? (
            <>
              <input
                type="text"
                className="text-primary w-full mb-2 px-3 py-2 text-3xl font-bold rounded"
                value={buyOnlineSection.heading}
                onChange={(e) => {
                  setPageContent((prev) => ({
                    ...prev,
                    sections: prev.sections.map((sec) =>
                      sec.key === "buy-entirely-online"
                        ? { ...sec, heading: e.target.value }
                        : sec
                    ),
                  }));
                }}
              />

              {/* Paragraphs editor */}
              {buyOnlineSection.paragraphs.map((para, idx) => (
                <textarea
                  key={idx}
                  className="text-primary w-full mb-2 px-3 py-2 rounded"
                  rows={3}
                  value={para}
                  onChange={(e) => {
                    const newParas = [...buyOnlineSection.paragraphs];
                    newParas[idx] = e.target.value;
                    setPageContent((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) =>
                        sec.key === "buy-entirely-online"
                          ? { ...sec, paragraphs: newParas }
                          : sec
                      ),
                    }));
                  }}
                />
              ))}
              <button
                className="mb-2 px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded"
                onClick={() => {
                  const newParas = [...buyOnlineSection.paragraphs, ""];
                  setPageContent((prev) => ({
                    ...prev,
                    sections: prev.sections.map((sec) =>
                      sec.key === "buy-entirely-online"
                        ? { ...sec, paragraphs: newParas }
                        : sec
                    ),
                  }));
                }}
              >
                + Add paragraph
              </button>

              {/* Image selector */}
              <div className="mb-2">
                <label className="block mb-1 text-sm font-medium text-primary">
                  Section Image
                </label>
                <PageImageSelector
                  pageKey="our-commitment"
                  value={buyOnlineSection.imageUrl}
                  onChange={(newUrl) => {
                    if (newUrl === "__upload_new__") {
                      buyOnlineFileInputRef.current.click();
                    } else {
                      setPageContent((prev) => ({
                        ...prev,
                        sections: prev.sections.map((sec) =>
                          sec.key === "buy-entirely-online"
                            ? { ...sec, imageUrl: newUrl }
                            : sec
                        ),
                      }));
                    }
                  }}
                />
                <input
                  type="file"
                  accept="image/*"
                  ref={buyOnlineFileInputRef}
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    uploadPageImage(file, (url) => {
                      setPageContent((prev) => ({
                        ...prev,
                        sections: prev.sections.map((sec) =>
                          sec.key === "buy-entirely-online"
                            ? { ...sec, imageUrl: url }
                            : sec
                        ),
                      }));
                    });
                    e.target.value = null;
                  }}
                />
              </div>

              <div className="flex space-x-2">
                <button
                  className="px-4 py-2 bg-green-600 text-white rounded"
                  onClick={() => saveContent({ ...pageContent })}
                >
                  Save
                </button>
                <button
                  className="px-4 py-2 bg-gray-600 text-white rounded"
                  onClick={() => setEditingSection(null)}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-brandAccent text-3xl md:text-4xl font-bold mb-4">
                {buyOnlineSection.heading}
              </h2>
              {buyOnlineSection.paragraphs.map((para, idx) => (
                <p key={idx} className="text-lg text-gray-800 mb-5">
                  {para}
                </p>
              ))}
              {buyOnlineSection.imageUrl && (
                <img
                  src={buyOnlineSection.imageUrl}
                  alt={buyOnlineSection.heading}
                  className="w-full object-contain rounded-3xl mt-6"
                />
              )}
            </>
          )}
        </div>
      </section>

      {/* ─── “Choose the way you pay” SECTION ───────────────────────────────────────── */}
      <section className="bg-white py-16 relative">
        {isAdmin && editingSection !== "choose-payment" && (
          <BiEdit
            onClick={() => setEditingSection("choose-payment")}
            className="absolute top-4 right-4 text-xl text-brandAccent cursor-pointer hover:text-accent"
            title="Edit Choose the way you pay"
          />
        )}
        <div className="max-w-5xl mx-auto px-4">
          {editingSection === "choose-payment" ? (
            <>
              <input
                type="text"
                className="text-primary w-full mb-2 px-3 py-2 text-2xl font-bold rounded"
                value={choosePaymentSection.heading}
                onChange={(e) => {
                  setPageContent((prev) => ({
                    ...prev,
                    sections: prev.sections.map((sec) =>
                      sec.key === "choose-payment"
                        ? { ...sec, heading: e.target.value }
                        : sec
                    ),
                  }));
                }}
              />
              {choosePaymentSection.paragraphs.map((para, idx) => (
                <textarea
                  key={idx}
                  className="text-primary w-full mb-2 px-3 py-2 rounded"
                  rows={2}
                  value={para}
                  onChange={(e) => {
                    const newParas = [...choosePaymentSection.paragraphs];
                    newParas[idx] = e.target.value;
                    setPageContent((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) =>
                        sec.key === "choose-payment"
                          ? { ...sec, paragraphs: newParas }
                          : sec
                      ),
                    }));
                  }}
                />
              ))}
              <button
                className="mb-2 px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded"
                onClick={() => {
                  const newParas = [...choosePaymentSection.paragraphs, ""];
                  setPageContent((prev) => ({
                    ...prev,
                    sections: prev.sections.map((sec) =>
                      sec.key === "choose-payment"
                        ? { ...sec, paragraphs: newParas }
                        : sec
                    ),
                  }));
                }}
              >
                + Add paragraph
              </button>

              {/* Links editor */}
              {choosePaymentSection.links.map((linkObj, idx) => (
                <div key={idx} className="mb-2 flex space-x-2">
                  <input
                    type="text"
                    className="text-primary flex-1 px-3 py-2 border rounded"
                    placeholder="Link text"
                    value={linkObj.text}
                    onChange={(e) => {
                      const newLinks = choosePaymentSection.links.map((l, i) =>
                        i === idx ? { ...l, text: e.target.value } : l
                      );
                      setPageContent((prev) => ({
                        ...prev,
                        sections: prev.sections.map((sec) =>
                          sec.key === "choose-payment"
                            ? { ...sec, links: newLinks }
                            : sec
                        ),
                      }));
                    }}
                  />
                  <input
                    type="text"
                    className="text-primary flex-1 px-3 py-2 border rounded"
                    placeholder="URL"
                    value={linkObj.href}
                    onChange={(e) => {
                      const newLinks = choosePaymentSection.links.map((l, i) =>
                        i === idx ? { ...l, href: e.target.value } : l
                      );
                      setPageContent((prev) => ({
                        ...prev,
                        sections: prev.sections.map((sec) =>
                          sec.key === "choose-payment"
                            ? { ...sec, links: newLinks }
                            : sec
                        ),
                      }));
                    }}
                  />
                  <button
                    className="px-2 text-red-600 hover:text-red-800"
                    onClick={() => {
                      const newLinks = choosePaymentSection.links.filter(
                        (_, i) => i !== idx
                      );
                      setPageContent((prev) => ({
                        ...prev,
                        sections: prev.sections.map((sec) =>
                          sec.key === "choose-payment"
                            ? { ...sec, links: newLinks }
                            : sec
                        ),
                      }));
                    }}
                  >
                    &times;
                  </button>
                </div>
              ))}
              <button
                className="mb-4 px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded"
                onClick={() => {
                  const newLinks = [
                    ...choosePaymentSection.links,
                    { text: "", href: "" },
                  ];
                  setPageContent((prev) => ({
                    ...prev,
                    sections: prev.sections.map((sec) =>
                      sec.key === "choose-payment"
                        ? { ...sec, links: newLinks }
                        : sec
                    ),
                  }));
                }}
              >
                + Add link
              </button>

              <div className="flex space-x-2">
                <button
                  className="px-4 py-2 bg-green-600 text-white rounded"
                  onClick={() => saveContent({ ...pageContent })}
                >
                  Save
                </button>
                <button
                  className="px-4 py-2 bg-gray-600 text-white rounded"
                  onClick={() => setEditingSection(null)}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col items-start mb-4 gap-3">
                <div className="text-brandAccent">
                  <LuNewspaper className="h-6 w-6" />
                </div>
                <h3 className="text-brandAccent text-2xl font-bold">
                  {choosePaymentSection.heading}
                </h3>
              </div>
              {choosePaymentSection.paragraphs.map((para, idx) => (
                <p key={idx} className="text-lg text-gray-800 mb-4">
                  {para}
                </p>
              ))}
              <ul className="list-disc list-inside space-y-2 text-primary text-lg font-bold">
                {choosePaymentSection.links.map((linkObj, idx) => (
                  <li key={idx}>
                    <Link
                      to={linkObj.href}
                      className="text-primary underline hover:opacity-90 transition"
                    >
                      {linkObj.text}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </section>

      {/* ─── “Know what you’re getting” SECTION ─────────────────────────────────────── */}
      <section className="bg-white pb-16 relative">
        {isAdmin && editingSection !== "know-getting" && (
          <BiEdit
            onClick={() => setEditingSection("know-getting")}
            className="absolute top-4 right-4 text-xl text-brandAccent cursor-pointer hover:text-accent"
            title="Edit Know what you’re getting"
          />
        )}
        <div className="max-w-5xl mx-auto px-4">
          {editingSection === "know-getting" ? (
            <>
              <div className="flex flex-col items-start mb-4 gap-3">
                <div className="text-brandAccent">
                  <BiSearchAlt className="h-8 w-8" />
                </div>
                <input
                  type="text"
                  className="text-primary w-full mb-2 px-3 py-2 text-2xl font-bold rounded"
                  value={knowGettingSection.heading}
                  onChange={(e) => {
                    setPageContent((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) =>
                        sec.key === "know-getting"
                          ? { ...sec, heading: e.target.value }
                          : sec
                      ),
                    }));
                  }}
                />
              </div>
              {knowGettingSection.paragraphs.map((para, idx) => (
                <textarea
                  key={idx}
                  className="text-primary w-full mb-2 px-3 py-2 rounded"
                  rows={2}
                  value={para}
                  onChange={(e) => {
                    const newParas = [...knowGettingSection.paragraphs];
                    newParas[idx] = e.target.value;
                    setPageContent((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) =>
                        sec.key === "know-getting"
                          ? { ...sec, paragraphs: newParas }
                          : sec
                      ),
                    }));
                  }}
                />
              ))}
              <button
                className="mb-2 px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded"
                onClick={() => {
                  const newParas = [...knowGettingSection.paragraphs, ""];
                  setPageContent((prev) => ({
                    ...prev,
                    sections: prev.sections.map((sec) =>
                      sec.key === "know-getting"
                        ? { ...sec, paragraphs: newParas }
                        : sec
                    ),
                  }));
                }}
              >
                + Add paragraph
              </button>

              {/* Links editor */}
              {knowGettingSection.links.map((linkObj, idx) => (
                <div key={idx} className="mb-2 flex space-x-2">
                  <input
                    type="text"
                    className="text-primary flex-1 px-3 py-2 border rounded"
                    placeholder="Link text"
                    value={linkObj.text}
                    onChange={(e) => {
                      const newLinks = knowGettingSection.links.map((l, i) =>
                        i === idx ? { ...l, text: e.target.value } : l
                      );
                      setPageContent((prev) => ({
                        ...prev,
                        sections: prev.sections.map((sec) =>
                          sec.key === "know-getting"
                            ? { ...sec, links: newLinks }
                            : sec
                        ),
                      }));
                    }}
                  />
                  <input
                    type="text"
                    className="text-primary flex-1 px-3 py-2 border rounded"
                    placeholder="URL"
                    value={linkObj.href}
                    onChange={(e) => {
                      const newLinks = knowGettingSection.links.map((l, i) =>
                        i === idx ? { ...l, href: e.target.value } : l
                      );
                      setPageContent((prev) => ({
                        ...prev,
                        sections: prev.sections.map((sec) =>
                          sec.key === "know-getting"
                            ? { ...sec, links: newLinks }
                            : sec
                        ),
                      }));
                    }}
                  />
                  <button
                    className="px-2 text-red-600 hover:text-red-800"
                    onClick={() => {
                      const newLinks = knowGettingSection.links.filter(
                        (_, i) => i !== idx
                      );
                      setPageContent((prev) => ({
                        ...prev,
                        sections: prev.sections.map((sec) =>
                          sec.key === "know-getting"
                            ? { ...sec, links: newLinks }
                            : sec
                        ),
                      }));
                    }}
                  >
                    &times;
                  </button>
                </div>
              ))}
              <button
                className="mb-4 px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded"
                onClick={() => {
                  const newLinks = [
                    ...knowGettingSection.links,
                    { text: "", href: "" },
                  ];
                  setPageContent((prev) => ({
                    ...prev,
                    sections: prev.sections.map((sec) =>
                      sec.key === "know-getting"
                        ? { ...sec, links: newLinks }
                        : sec
                    ),
                  }));
                }}
              >
                + Add link
              </button>

              <div className="flex space-x-2">
                <button
                  className="px-4 py-2 bg-green-600 text-white rounded"
                  onClick={() => saveContent({ ...pageContent })}
                >
                  Save
                </button>
                <button
                  className="px-4 py-2 bg-gray-600 text-white rounded"
                  onClick={() => setEditingSection(null)}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col items-start mb-4 gap-3">
                <div className="text-brandAccent">
                  <BiSearchAlt className="h-8 w-8" />
                </div>
                <h3 className="text-brandAccent text-2xl font-bold">
                  {knowGettingSection.heading}
                </h3>
              </div>
              {knowGettingSection.paragraphs.map((para, idx) => (
                <p key={idx} className="text-lg text-gray-800 mb-4">
                  {para}
                </p>
              ))}
              <ul className="list-disc list-inside space-y-2 text-primary font-bold">
                {knowGettingSection.links.map((linkObj, idx) => (
                  <li key={idx}>
                    <Link
                      to={linkObj.href}
                      className="text-primary text-lg underline hover:opacity-90 transition"
                    >
                      {linkObj.text}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </section>

      {/* ─── “Need more info?” SECTION ─────────────────────────────────────── */}
      <section className="bg-white pb-16 relative">
        {isAdmin && editingSection !== "need-more-info" && (
          <BiEdit
            onClick={() => setEditingSection("need-more-info")}
            className="absolute top-4 right-4 text-xl text-brandAccent cursor-pointer hover:text-accent"
            title="Edit Need more info?"
          />
        )}
        <div className="max-w-5xl mx-auto px-4">
          {editingSection === "need-more-info" ? (
            <>
              <div className="flex flex-col items-start mb-4 gap-3">
                <div className="text-brandAccent">
                  <BsChat className="h-6 w-6" />
                </div>
                <input
                  type="text"
                  className="text-primary w-full mb-2 px-3 py-2 text-2xl font-bold rounded"
                  value={needMoreInfoSection.heading}
                  onChange={(e) => {
                    setPageContent((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) =>
                        sec.key === "need-more-info"
                          ? { ...sec, heading: e.target.value }
                          : sec
                      ),
                    }));
                  }}
                />
              </div>
              {needMoreInfoSection.paragraphs.map((para, idx) => (
                <textarea
                  key={idx}
                  className="text-primary w-full mb-2 px-3 py-2 rounded"
                  rows={2}
                  value={para}
                  onChange={(e) => {
                    const newParas = [...needMoreInfoSection.paragraphs];
                    newParas[idx] = e.target.value;
                    setPageContent((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) =>
                        sec.key === "need-more-info"
                          ? { ...sec, paragraphs: newParas }
                          : sec
                      ),
                    }));
                  }}
                />
              ))}
              <button
                className="mb-2 px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded"
                onClick={() => {
                  const newParas = [...needMoreInfoSection.paragraphs, ""];
                  setPageContent((prev) => ({
                    ...prev,
                    sections: prev.sections.map((sec) =>
                      sec.key === "need-more-info"
                        ? { ...sec, paragraphs: newParas }
                        : sec
                    ),
                  }));
                }}
              >
                + Add paragraph
              </button>

              {/* Links editor */}
              {needMoreInfoSection.links.map((linkObj, idx) => (
                <div key={idx} className="mb-2 flex space-x-2">
                  <input
                    type="text"
                    className="text-primary flex-1 px-3 py-2 border rounded"
                    placeholder="Link text"
                    value={linkObj.text}
                    onChange={(e) => {
                      const newLinks = needMoreInfoSection.links.map((l, i) =>
                        i === idx ? { ...l, text: e.target.value } : l
                      );
                      setPageContent((prev) => ({
                        ...prev,
                        sections: prev.sections.map((sec) =>
                          sec.key === "need-more-info"
                            ? { ...sec, links: newLinks }
                            : sec
                        ),
                      }));
                    }}
                  />
                  <input
                    type="text"
                    className="text-primary flex-1 px-3 py-2 border rounded"
                    placeholder="URL"
                    value={linkObj.href}
                    onChange={(e) => {
                      const newLinks = needMoreInfoSection.links.map((l, i) =>
                        i === idx ? { ...l, href: e.target.value } : l
                      );
                      setPageContent((prev) => ({
                        ...prev,
                        sections: prev.sections.map((sec) =>
                          sec.key === "need-more-info"
                            ? { ...sec, links: newLinks }
                            : sec
                        ),
                      }));
                    }}
                  />
                  <button
                    className="px-2 text-red-600 hover:text-red-800"
                    onClick={() => {
                      const newLinks = needMoreInfoSection.links.filter(
                        (_, i) => i !== idx
                      );
                      setPageContent((prev) => ({
                        ...prev,
                        sections: prev.sections.map((sec) =>
                          sec.key === "need-more-info"
                            ? { ...sec, links: newLinks }
                            : sec
                        ),
                      }));
                    }}
                  >
                    &times;
                  </button>
                </div>
              ))}
              <button
                className="mb-4 px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded"
                onClick={() => {
                  const newLinks = [
                    ...needMoreInfoSection.links,
                    { text: "", href: "" },
                  ];
                  setPageContent((prev) => ({
                    ...prev,
                    sections: prev.sections.map((sec) =>
                      sec.key === "need-more-info"
                        ? { ...sec, links: newLinks }
                        : sec
                    ),
                  }));
                }}
              >
                + Add link
              </button>

              <div className="flex space-x-2">
                <button
                  className="px-4 py-2 bg-green-600 text-white rounded"
                  onClick={() => saveContent({ ...pageContent })}
                >
                  Save
                </button>
                <button
                  className="px-4 py-2 bg-gray-600 text-white rounded"
                  onClick={() => setEditingSection(null)}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col items-start mb-4 gap-3">
                <div className="text-brandAccent">
                  <BsChat className="h-6 w-6" />
                </div>
                <h3 className="text-brandAccent text-2xl font-bold">
                  {needMoreInfoSection.heading}
                </h3>
              </div>
              {needMoreInfoSection.paragraphs.map((para, idx) => (
                <p key={idx} className="text-lg text-gray-800 mb-4">
                  {para}
                </p>
              ))}
              <ul className="list-disc list-inside space-y-2 text-primary font-bold">
                {needMoreInfoSection.links.map((linkObj, idx) => (
                  <li key={idx}>
                    <Link
                      to={linkObj.href}
                      className="text-primary text-lg underline hover:opacity-90 transition"
                    >
                      {linkObj.text}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </section>

      {/* ─── “Need help choosing?” SECTION ───────────────────────────────────────── */}
      <section className="bg-white/95 py-20 relative">
        {isAdmin && editingSection !== "need-help-choosing" && (
          <BiEdit
            onClick={() => setEditingSection("need-help-choosing")}
            className="absolute top-4 right-4 text-xl text-brandAccent cursor-pointer hover:text-accent"
            title="Edit Need help choosing?"
          />
        )}
        <div className="max-w-5xl mx-auto px-4">
          {editingSection === "need-help-choosing" ? (
            <>
              <input
                type="text"
                className="text-primary w-full mb-2 px-3 py-2 text-2xl font-bold rounded"
                value={needHelpChoosingSection.heading}
                onChange={(e) => {
                  setPageContent((prev) => ({
                    ...prev,
                    sections: prev.sections.map((sec) =>
                      sec.key === "need-help-choosing"
                        ? { ...sec, heading: e.target.value }
                        : sec
                    ),
                  }));
                }}
              />
              {needHelpChoosingSection.paragraphs.map((para, idx) => (
                <textarea
                  key={idx}
                  className="text-primary w-full mb-2 px-3 py-2 rounded"
                  rows={2}
                  value={para}
                  onChange={(e) => {
                    const newParas = [...needHelpChoosingSection.paragraphs];
                    newParas[idx] = e.target.value;
                    setPageContent((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) =>
                        sec.key === "need-help-choosing"
                          ? { ...sec, paragraphs: newParas }
                          : sec
                      ),
                    }));
                  }}
                />
              ))}
              <button
                className="mb-2 px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded"
                onClick={() => {
                  const newParas = [...needHelpChoosingSection.paragraphs, ""];
                  setPageContent((prev) => ({
                    ...prev,
                    sections: prev.sections.map((sec) =>
                      sec.key === "need-help-choosing"
                        ? { ...sec, paragraphs: newParas }
                        : sec
                    ),
                  }));
                }}
              >
                + Add paragraph
              </button>

              {/* Single link editor */}
              <div className="mb-4 flex space-x-2">
                <input
                  type="text"
                  className="text-primary flex-1 px-3 py-2 border rounded"
                  placeholder="Button text"
                  value={needHelpChoosingSection.link.text}
                  onChange={(e) => {
                    setPageContent((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) =>
                        sec.key === "need-help-choosing"
                          ? {
                              ...sec,
                              link: { ...sec.link, text: e.target.value },
                            }
                          : sec
                      ),
                    }));
                  }}
                />
                <input
                  type="text"
                  className="text-primary flex-1 px-3 py-2 border rounded"
                  placeholder="URL"
                  value={needHelpChoosingSection.link.href}
                  onChange={(e) => {
                    setPageContent((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) =>
                        sec.key === "need-help-choosing"
                          ? {
                              ...sec,
                              link: { ...sec.link, href: e.target.value },
                            }
                          : sec
                      ),
                    }));
                  }}
                />
              </div>

              <div className="flex space-x-2">
                <button
                  className="px-4 py-2 bg-green-600 text-white rounded"
                  onClick={() => saveContent({ ...pageContent })}
                >
                  Save
                </button>
                <button
                  className="px-4 py-2 bg-gray-600 text-white rounded"
                  onClick={() => setEditingSection(null)}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {needHelpChoosingSection.heading}
              </h2>
              {needHelpChoosingSection.paragraphs.map((para, idx) => (
                <p key={idx} className="text-lg text-gray-700 mb-4">
                  {para}
                </p>
              ))}
              <Link
                to={needHelpChoosingSection.link.href}
                className="inline-block px-8 py-3 border-2 border-accent rounded-full text-lg font-medium text-accent hover:bg-accent hover:text-white transition"
              >
                {needHelpChoosingSection.link.text}
              </Link>
            </>
          )}
        </div>
      </section>

      {/* ─── “Ready to hit the open road?” SECTION ──────────────────────────────────── */}
      <section className="bg-primary py-20 relative">
        {isAdmin && editingSection !== "ready-open-road" && (
          <BiEdit
            onClick={() => setEditingSection("ready-open-road")}
            className="absolute top-4 right-4 text-xl text-brandAccent cursor-pointer hover:text-accent"
            title="Edit Ready section"
          />
        )}
        <div className="max-w-5xl mx-auto px-4">
          {editingSection === "ready-open-road" ? (
            <>
              <input
                type="text"
                className="text-primary w-full mb-2 px-3 py-2 text-lg rounded"
                placeholder="Preamble (small text)"
                value={readySection.preamble}
                onChange={(e) => {
                  setPageContent((prev) => ({
                    ...prev,
                    sections: prev.sections.map((sec) =>
                      sec.key === "ready-open-road"
                        ? { ...sec, preamble: e.target.value }
                        : sec
                    ),
                  }));
                }}
              />
              <textarea
                className="text-primary w-full mb-2 px-3 py-2 rounded"
                rows={2}
                placeholder="Heading"
                value={readySection.heading}
                onChange={(e) => {
                  setPageContent((prev) => ({
                    ...prev,
                    sections: prev.sections.map((sec) =>
                      sec.key === "ready-open-road"
                        ? { ...sec, heading: e.target.value }
                        : sec
                    ),
                  }));
                }}
              />
              <div className="mb-4 grid grid-cols-2 gap-2">
                <input
                  type="text"
                  className="text-primary px-3 py-2 border rounded"
                  placeholder="Button text"
                  value={readySection.button.text}
                  onChange={(e) => {
                    setPageContent((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) =>
                        sec.key === "ready-open-road"
                          ? {
                              ...sec,
                              button: { ...sec.button, text: e.target.value },
                            }
                          : sec
                      ),
                    }));
                  }}
                />
                <input
                  type="text"
                  className="text-primary px-3 py-2 border rounded"
                  placeholder="Button URL"
                  value={readySection.button.href}
                  onChange={(e) => {
                    setPageContent((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) =>
                        sec.key === "ready-open-road"
                          ? {
                              ...sec,
                              button: { ...sec.button, href: e.target.value },
                            }
                          : sec
                      ),
                    }));
                  }}
                />
              </div>
              <div className="flex space-x-2">
                <button
                  className="px-4 py-2 bg-green-600 text-white rounded"
                  onClick={() => saveContent({ ...pageContent })}
                >
                  Save
                </button>
                <button
                  className="px-4 py-2 bg-gray-600 text-white rounded"
                  onClick={() => setEditingSection(null)}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-6">
                <div className="flex items-center space-x-3">
                  <BiSearch className="text-4xl text-brandAccent" />
                  <div>
                    <p className="text-lg text-brandAccent">
                      {readySection.preamble}
                    </p>
                    <h2 className="text-2xl md:text-3xl font-bold text-secondary">
                      {readySection.heading}
                    </h2>
                  </div>
                </div>
                <div className="flex justify-start md:justify-end">
                  <Link
                    to={readySection.button.href}
                    className="text-lg inline-block bg-brandAccent hover:bg-accent text-white font-semibold px-20 py-3 rounded-full transition"
                  >
                    {readySection.button.text}
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}
